/**
 * Assigns computed start/end dates to tasks that lack planned_start_date.
 * Forward-pass с поддержкой dep_type (Roxber Wave 7+ Phase 2).
 *
 * Семантика типов связей:
 *   - finish_to_start (КН): start_succ >= end_pred + lag
 *   - start_to_start (НН):  start_succ >= start_pred + lag
 *   - finish_to_finish (КК): start_succ >= end_pred + lag - dur_succ
 *
 * При нескольких входящих связях берём MAX (самое позднее из ограничений).
 * Read-only — не пишет в БД.
 */
export function computeSchedule(projects, tasks, dependencies) {
  const projMap = Object.fromEntries(projects.map(p => [p.id, p]));
  const taskMap = Object.fromEntries(tasks.map(t => [t.id, t]));

  // successor_id → [{pid, type, lag}]
  const predDetailMap = {};
  for (const d of dependencies) {
    (predDetailMap[d.successor_id] ??= []).push({
      pid:  d.predecessor_id,
      type: d.dep_type ?? 'finish_to_start',
      lag:  parseFloat(d.lag_days) || 0,
    });
  }

  const computed  = {};
  const computing = new Set(); // cycle guard

  const DAY_MS = 86400000;

  function compute(taskId) {
    if (computed[taskId])  return computed[taskId];
    if (computing.has(taskId)) {
      // cycle detected — fallback to today
      const now = new Date();
      return { start: now, end: new Date(now.getTime() + DAY_MS) };
    }
    computing.add(taskId);

    const task = taskMap[taskId];
    if (!task) {
      computing.delete(taskId);
      const now = new Date();
      return { start: now, end: new Date(now.getTime() + DAY_MS) };
    }

    // Milestone: zero-duration anchor at its deadline.
    if (task.is_milestone && task.deadline) {
      const d = new Date(task.deadline);
      computing.delete(taskId);
      computed[taskId] = { start: d, end: d };
      return computed[taskId];
    }

    const durDays = parseFloat(task.duration_days) || 1;
    const durMs   = durDays * DAY_MS;

    let start;
    if (task.planned_start_date) {
      start = new Date(task.planned_start_date);
      if (task.planned_start_time) {
        const [h, m, s] = task.planned_start_time.split(':').map(Number);
        start.setHours(h || 0, m || 0, s || 0, 0);
      }
    } else {
      const preds = predDetailMap[task.id] ?? [];
      if (preds.length === 0) {
        const proj = projMap[task.project_id];
        start = proj?.start_date ? new Date(proj.start_date) : new Date();
      } else {
        // Forward-pass: max нижних оценок в зависимости от dep_type.
        const candidates = preds.map(({pid, type, lag}) => {
          const p = compute(pid);
          const lagMs = lag * DAY_MS;
          if (type === 'start_to_start')   return p.start.getTime() + lagMs;
          if (type === 'finish_to_finish') return p.end.getTime()   + lagMs - durMs;
          // finish_to_start (default)
          return p.end.getTime() + lagMs;
        });
        start = new Date(Math.max(...candidates));
      }
    }

    const end = task.planned_end_date
      ? new Date(task.planned_end_date)
      : new Date(start.getTime() + durMs);

    computing.delete(taskId);
    computed[taskId] = { start, end };
    return computed[taskId];
  }

  for (const task of tasks) {
    compute(task.id);
  }

  return tasks.map(t => ({ ...t, ...computed[t.id] }));
}
